<template>
    <div id="issue-container" class="h-100">
        <transition name="fade" mode="out-in">
            <div key="loading-container" v-if="isLoading" class="loading-screen h-100">
                <div class="header-placeholder"></div>
                <div
                    class="d-flex justify-content-center align-items-center"
                    :class="[locationType == 'viewer' ? 'loading-container-viewer' : 'loading-container']"
                >
                    <b-spinner variant="secondary" small />
                </div>
            </div>
            <div key="data-container" class="h-100" v-else>
                <div v-if="locationType != 'viewer'" class="top-buttons d-flex justify-content-end w-100">
                    <ci-button @click="$refs['report-modal'].show(issueId)" normalCase :text="$t('Views_Project_Issues_IssuesNewScreen_Report_1')" />
                </div>

                <div
                    v-if="queryData"
                    :class="[locationType == 'viewer' ? 'selected-info-container h-100' : 'issue-details', messageInputFilePreviewsActive && 'is-files-active']"
                >
                    <div class="issue-grid h-100" :class="{ 'issue-grid-editing': editModeEnabled }">
                        <IssueHeader
                            :selectedIssue="queryData.getIssue"
                            :responsiblesOptions="responsiblesOptions"
                            :subjectsOptions="subjectsOptions"
                            :members="members"
                            :editModeEnabled="editModeEnabled"
                            :locationType="locationType"
                            :isBlacklisted="isBlacklisted"
                            @editing="(queryData) => (editModeEnabled = queryData)"
                            @issueUpdated="updateList"
                            @onStatusChange="onSelectedIssueStatusChange"
                            @onIssueDeleted="onIssueDeleted"
                        />

                        <IssueComments
                            v-if="!editModeEnabled"
                            ref="issue-comments"
                            :selectedIssue="queryData.getIssue"
                            :loggedUser="getUser"
                            :locationType="locationType"
                            @onIssueCommentUpdated="updateIssueComment"
                            @onIssueCommentDeleted="deleteIssueComment"
                        />
                    </div>
                    <CustomMessageInput
                        :selectedIssue="queryData.getIssue"
                        @messageSent="addNewComment"
                        :locationType="locationType"
                        @onShowFilePreviews="toggleMessageInputFilePreviews"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// Components
import CustomMessageInput from '@/components/Utils/CustomMessageInput'
import IssueComments from '@/components/Issues/IssueComments'
import IssueHeader from '@/components/Issues/IssueHeader'

import { mapGetters, mapState } from 'vuex'

// ==================
// JSON data
// ==================
import getIssueOptionsToEdit from '@/data/getIssueOptionsToEdit'

export default {
    props: {
        issueId: {
            type: Number,
        },
        locationType: {
            type: String,
        },
        issue_count: {
            type: Object,
        },
        issueOptions: {
            type: Object,
        },
    },
    components: {
        IssueHeader,
        IssueComments,
        CustomMessageInput,
    },

    computed: {
        ...mapState(['sidebar']),
        ...mapGetters('demo', ['getUser']),
        isArchived() {
            return false
        },

        /**
         * Summary.
         * Verify project and user to hardcode deny permission to interact with issues.
         * TODO: remove this when custom permissions are done
         */
        isBlacklisted() {
            let projectId = Number(this.$route.params.id || this.$route.params.project_id)
            let blacklistedProjects = [20, 854]
            let blacklistedEmails = [
                'tatiana.goncalves@vs.unimed.com.br',
                'lindomar.carlos@vs.unimed.com.br',
                'dirceu.filho@vs.unimed.com.br',
                'renato@obragerenciada.com.br',
                'sabrina@obragerenciada.com.br',
                'queizy.sartori@constructin.com.br',
            ]

            if (!blacklistedProjects.includes(projectId)) {
                return false
            }

            if (!blacklistedEmails.includes(this.getUser.email)) {
                return false
            }

            return true
        },
    },
    watch: {
        issueId() {
            // this.isLoading = true
            this.queryData = this.$store.state.demo.getIssue[this.issueId].data
        },
    },
    data() {
        return {
            isLoading: false,
            queryData: this.$store.state.demo.getIssue[this.issueId].data,
            // getIssue,

            editModeEnabled: false,
            responsiblesOptions: [],
            subjectsOptions: [],
            members: [],
            messageInputFilePreviewsActive: false,
        }
    },
    methods: {
        // ==============================
        // New methods, JSON local
        // ==============================
        deleteIssueComment(comment) {
            this.queryData.getIssue.events = this.queryData.getIssue.events.filter((event) => {
                return event.id != comment.id
            })
        },
        updateIssueComment(comment) {
            this.queryData.getIssue.events.map((event) => {
                if (comment.id == event.id) {
                    event.comment = {
                        ...event.comment,
                        ...comment,
                    }
                }
            })
        },
        addNewComment(comment) {
            const id = this.queryData.getIssue.events.length == 0 ? 1 : this.queryData.getIssue.events.at(-1).id + 1
            let commentObject = {
                id: id,
                type: 'comment',
                created_at: new Date().toLocaleString(),
                comment: {
                    ...comment,
                    id: id,
                },
                created_by: this.getUser,
            }
            this.queryData.getIssue.events.push({ ...commentObject })
        },
        // ==============================
        toggleMessageInputFilePreviews(filesLength) {
            this.messageInputFilePreviewsActive = Boolean(filesLength)
        },

        updateList() {
            this.$emit('issueUpdated')
        },
        onSelectedIssueStatusChange({ issue, status, old_status }) {
            this.$emit('onStatusChange', { issue, status, old_status })
            if (this.queryData.getIssue.status == status) return

            this.queryData.getIssue.status = status

            const id = this.queryData.getIssue.events.length > 0 ? this.queryData.getIssue.events.at(-1).id + 1 : Math.ceil(Math.random() * 1000)

            let commentObject = {
                id,
                type: status == 'open' ? 'reopen' : status == 'in_review' ? 'to_review' : 'closed',
                created_at: new Date().toLocaleString(),
                comment: {
                    attachments: [],
                    edited: false,
                    text: null,
                    id,
                },
                created_by: {
                    id: 719,
                    name: 'Bernard',
                    surname: 'pacheco',
                    image: 'https://firebasestorage.googleapis.com/v0/b/constructin-15d0f.appspot.com/o/users%2F58f82db1-f106-4cbb-8036-60800a527321.jpeg?alt=media&token=9d9f27b7-44fc-4a8c-a21f-403e81e39fbb',
                    email: 'bernardo.fonpac@gmail.com',
                },
            }
            this.queryData.getIssue.events.push({ ...commentObject })

            this.$store.commit('demo/UPDATE_STATUS_ISSUES_COUNT', { issue, status, old_status })
        },
        onIssueDeleted(issueId) {
            this.$emit('onIssueDeleted', issueId)
        },

        // UTILS
        generateResponsiblesOptions(groups) {
            this.responsiblesOptions = groups.map((group) => {
                return {
                    id: 'group' + group.id,
                    label: group.name ? group.name : this.$t('keyword86'),
                    children: group.members.map((member) => {
                        return {
                            id: member.id,
                            label: member.user ? member.user.name + ' ' + member.user.surname : member.email,
                        }
                    }),
                }
            })
        },
        generateSubjectsOptions(subjects) {
            this.subjectsOptions = subjects.map((subject) => {
                return {
                    id: subject.id,
                    label: subject.code + '-' + subject.name,
                }
            })
        },
    },
    mounted() {
        let data = getIssueOptionsToEdit.data
        this.members = data.getProject.members
        this.generateResponsiblesOptions(data.getProject.groups)
        this.generateSubjectsOptions(data.getProject.subjects)
    },
}
</script>

<style lang="scss" scoped>
#issue-container {
    .loading-screen {
        .header-placeholder {
            height: 48px;
        }
        .loading-container {
            height: calc(100% - 48px);
            background: var(--primary-3);
            border-left: 2px solid var(--primary-1);
            border-radius: 0 4px 4px 0;
            opacity: 0.8;
        }
        .loading-container-viewer {
            height: calc(100% - 48px);
            opacity: 0.8;
        }
    }
    .top-buttons {
        height: 48px;
    }
    .issue-details {
        border-left: 2px solid var(--primary-1);
        height: calc(100% - 48px);
        background-color: var(--primary-2);
        border-radius: 0 4px 4px 0;
        display: grid;
        grid-template-rows: minmax(0, 1fr) max-content;
        .issue-grid {
            display: grid;
            grid-template-rows: max-content minmax(0, 1fr);
            &.issue-grid-editing {
                grid-template-rows: initial;
            }
        }
    }

    .selected-info-container {
        display: grid;
        grid-template-rows: minmax(0, 1fr) max-content;
        .issue-grid {
            height: calc(100vh - 80px - 62px);
            display: grid;
            grid-template-rows: max-content minmax(0, 1fr);

            #issue-comments {
                background-color: var(--primary-1);
            }
        }

        &.is-files-active {
            #custom-message-input-container {
                height: 230px;
                position: absolute;
                bottom: 0;
                width: 100%;
                border-top: 2px solid var(--secondary-1);
            }
        }

        #custom-message-input-container {
            height: 62px;
            max-width: 316px;
        }
    }
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.25s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
        opacity: 0;
    }
}
</style>
